import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type {
  DashboardPrototypeDto,
  PrototypeDeployStatus,
} from '@aiai/shared';
import { fetchMyPrototypes } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { formatRelative } from '../lib/format';
import { workspacePath } from '../lib/workspacePath';
import { groupByWorkspace } from '../lib/groupByWorkspace';
import { useOrg } from '../org/useOrg';
import { useCapabilities } from '../hooks/useCapabilities';
import { NewPrototypeModal } from '../components/NewPrototypeModal';
import { WorkspaceGroupHeader } from '../components/WorkspaceGroupHeader';
import { PageHeader } from '../components/PageHeader';
import { IncomingTransfersBanner } from '../components/IncomingTransfersBanner';
import { Row, RowList } from '../components/Row';
import { Spinner } from '../components/Spinner';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// Statuses that are still in flight — while any prototype is in one of these,
// poll so the deploy status advances without a manual refresh.
const IN_FLIGHT_STATUSES = new Set(['provisioning', 'building']);

// The human word for each deploy state — the "is it live?" signal, shown in the
// row's description so it reads in plain words next to the dot's colour.
const STATUS_LABEL: Record<PrototypeDeployStatus, string> = {
  provisioning: 'Provisioning',
  building: 'Building',
  live: 'Live',
  failed: 'Deploy failed',
  stopped: 'Stopped',
  sleeping: 'Sleeping',
};

function PrototypeRow({ prototype }: { prototype: DashboardPrototypeDto }) {
  const { deployStatus, liveUrl, updatedAt } = prototype.deployment;
  const displayUrl = liveUrl.replace(/^https?:\/\//, '');
  const statusLabel = STATUS_LABEL[deployStatus] ?? deployStatus;

  // One muted line: the live state in words, then the live URL — the extra
  // "it's deployed and reachable" info a repo project doesn't have.
  const text = displayUrl ? `${statusLabel} · ${displayUrl}` : statusLabel;
  // In-flight deploys get a spinner so the list reads as loading at a glance.
  const description = IN_FLIGHT_STATUSES.has(deployStatus) ? (
    <span className="inline-flex items-center gap-1.5">
      <Spinner className="h-3 w-3 shrink-0" />
      <span>{text}</span>
    </span>
  ) : (
    text
  );

  return (
    <Row
      to={workspacePath(prototype.workspace.slug, `/prototypes/${prototype.code ?? prototype.id}`)}
      badge={<StatusBadge status={deployStatus} size="sm" />}
      title={prototype.name}
      description={description}
      timestamp={formatRelative(updatedAt)}
    />
  );
}

export function PrototypesPage() {
  useDocumentTitle('Prototypes');
  const navigate = useNavigate();
  const { orgs } = useOrg();
  const { workspacesWith } = useCapabilities();
  // Only workspaces the caller may manage prototypes in are eligible targets.
  const creatable = workspacesWith('prototype:manage');
  const canCreate = creatable.length > 0;
  const [modalOpen, setModalOpen] = useState(false);

  const prototypesQuery = useQuery({
    queryKey: qk.myPrototypes,
    queryFn: fetchMyPrototypes,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((p) =>
        IN_FLIGHT_STATUSES.has(p.deployment.deployStatus),
      )
        ? 3000
        : false,
  });

  const groups = useMemo(
    () => groupByWorkspace(prototypesQuery.data ?? []),
    [prototypesQuery.data],
  );

  // The create modal carries its own workspace selector; opening it directly is
  // enough, and it hands back the chosen workspace slug to route into on create.
  const newButton = canCreate ? (
    <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
      <Plus className="h-4 w-4" aria-hidden="true" />
      New prototype
    </button>
  ) : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prototypes"
        subtitle="Throwaway apps your agents build and deploy, across every workspace."
        actions={newButton}
      />

      <IncomingTransfersBanner kind="prototype" />

      {prototypesQuery.isLoading ? (
        <LoadingState label="Loading prototypes…" />
      ) : prototypesQuery.isError ? (
        <ErrorState
          message={errorMessage(prototypesQuery.error, 'Failed to load prototypes')}
          onRetry={() => prototypesQuery.refetch()}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No prototypes yet."
          description={
            canCreate
              ? 'Describe an idea and let an agent scaffold, deploy, and iterate on it.'
              : orgs.length === 0
                ? 'You are not a member of any workspace yet. Ask an admin to invite you.'
                : 'No prototypes here yet. A workspace owner can create one.'
          }
          action={newButton}
        />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.workspace.id}>
              <WorkspaceGroupHeader
                workspace={group.workspace}
                right={`${group.items.length} prototype${group.items.length === 1 ? '' : 's'}`}
              />
              <RowList>
                {group.items.map((p) => (
                  <PrototypeRow key={p.id} prototype={p} />
                ))}
              </RowList>
            </div>
          ))}
        </div>
      )}

      {canCreate && (
        <NewPrototypeModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={(p, workspaceSlug) =>
            navigate(workspacePath(workspaceSlug, `/prototypes/${p.code ?? p.id}`))
          }
        />
      )}
    </div>
  );
}
